import {
  Button,
  Checkbox,
  ConfirmDialog,
  EmptyState,
  Field,
  FilterSegments,
  PageHeader,
  PageTitle,
  RowActions,
  SearchInput,
  StatusTag,
  SystemBanner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
  Textarea,
  BulkBar,
  formatCurrency,
  titleCase,
  toast,
  useBreakpoint,
  type FilterSegmentOption,
} from '@westhetics/ui'
import { useId, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { ListFooter } from '@/app/list-footer'
import { LoadError } from '@/app/load-error'
import { PeriodFilter } from '@/app/period-filter'
import { Page } from '@/app/shell'
import { errorMessage } from '@/lib/errors'
import { useSession } from '@/lib/session'
import { useDebounced } from '@/lib/use-debounced'
import { useStoredFilter } from '@/lib/use-stored-filter'
import { routes } from '@/routes'
import {
  saveDownloadedFile,
  type SignatureFilter,
  type SignatureRow,
  type SignatureSort,
  useBatchResend,
  useCancelSignature,
  useDeleteSignedDocument,
  useSignatures,
  useSignaturesZip,
} from './api'
import {
  CRITERION,
  FILTER_LABELS,
  FILTERS,
  SORT_NOTES,
  batchToast,
  daysOpenText,
  sentCell,
  subtitle,
} from './labels'
import { useSignatureLive } from './live'
import { PUSH_MESSAGES, usePushNotifications } from './push'
import { SignaturePanel } from './signature-panel'
import { useSignatureActions, type ActionTarget } from './use-signature-actions'

/**
 * Assinaturas (docs/areas/documentos-e-assinaturas.md, "Lista de assinaturas"; RN-ASS-12, RN-ASS-13).
 * Arquétipo: lista de trabalho. A pergunta: "o que precisa ser resolvido hoje?". Abre em Pendentes, por
 * gravidade; a linha abre o painel; a seleção em lote troca o cabeçalho pela barra; o que a paciente
 * faz chega na hora (RN-ASS-32), e o aviso no navegador avisa mesmo com a aba fechada.
 */

function targetOf(row: SignatureRow): ActionTarget {
  return {
    appointmentId: row.appointmentId,
    patientName: row.patient.name,
    // A lista não traz o contato: o diálogo de reenvio o pede ao painel quando precisar; aqui, os dois
    // canais ficam abertos e a API confere (RN-ASS-02).
    contact: {
      whatsapp: row.channels === 'email' ? null : 'x',
      email: row.channels === 'whatsapp' ? null : 'x',
    },
    status: row.status,
    actions: row.actions,
    documentCount: row.documents.length,
  }
}

const SORT_BY_HEAD: Record<string, [SignatureSort, SignatureSort]> = {
  sentAt: ['-sentAt', 'sentAt'],
  patient: ['patient', 'severity'],
  daysOpen: ['-daysOpen', 'severity'],
  total: ['-totalAmountCents', 'severity'],
}

export function SignaturesPage() {
  const navigate = useNavigate()
  const session = useSession()
  const isDesk = useBreakpoint('desk')
  const isWide = useBreakpoint('wide')
  const [params, setParams] = useSearchParams()
  const stored = useStoredFilter('signatures', FILTERS, 'pending')
  const urlFilter = params.get('filter')
  const filter: SignatureFilter = (FILTERS as readonly string[]).includes(urlFilter ?? '')
    ? (urlFilter as SignatureFilter)
    : stored.filter
  const [period, setPeriod] = useState<{ from?: string; to?: string }>({})
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<SignatureSort>('severity')
  const [page, setPage] = useState(1)
  const term = useDebounced(search.trim())
  const query = useSignatures({
    filter,
    from: period.from,
    to: period.to,
    search: term.length >= 2 ? term.slice(0, 120) : undefined,
    sort,
    page,
  })
  const meta = query.data?.meta
  const rows = query.data?.data ?? []
  const whatsappMode = meta?.whatsappMode ?? 'manual'
  const provider = meta?.signatureProvider ?? 'westhetics'
  const selectedId = params.get('request')
  const [selection, setSelection] = useState<Set<string>>(() => new Set())
  const [bulkCancel, setBulkCancel] = useState(false)
  const [bulkDelete, setBulkDelete] = useState<string | null>(null)
  const justificationId = useId()
  const actions = useSignatureActions({ whatsappMode, provider })
  const batch = useBatchResend()
  const zip = useSignaturesZip()
  const cancel = useCancelSignature()
  const remove = useDeleteSignedDocument()
  const push = usePushNotifications()
  useSignatureLive()

  function setFilter(next: SignatureFilter) {
    stored.setFilter(next)
    setPage(1)
    setSelection(new Set())
    const nextParams = new URLSearchParams(params)
    nextParams.delete('filter')
    setParams(nextParams, { replace: true })
  }

  function openPanel(appointmentId: string | null) {
    const nextParams = new URLSearchParams(params)
    if (appointmentId) nextParams.set('request', appointmentId)
    else nextParams.delete('request')
    setParams(nextParams, { replace: true })
  }

  function sortBy(head: keyof typeof SORT_BY_HEAD) {
    const [first, second] = SORT_BY_HEAD[head]!
    setSort(sort === first ? second : first)
    setPage(1)
  }

  function toggle(id: string) {
    setSelection((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const selectedRows = rows.filter((r) => selection.has(r.appointmentId))
  const ids = [...selection]

  function resendBatch(channels: 'whatsapp' | 'email') {
    batch.mutate(
      { appointmentIds: ids, channels },
      {
        onSuccess: (result) => {
          setSelection(new Set())
          toast(batchToast(result, channels), { undo: actions.undo(result.actionId) })
        },
        onError: (error) => toast(errorMessage(error)),
      },
    )
  }

  function downloadZip() {
    zip.mutate(ids, {
      onSuccess: (file) => saveDownloadedFile(file, 'assinaturas.zip'),
      onError: (error) => toast(errorMessage(error)),
    })
  }

  async function cancelBatch() {
    let done = 0
    for (const row of selectedRows) {
      if (!row.actions.includes('cancel')) continue
      try {
        await cancel.mutateAsync({ appointmentId: row.appointmentId })
        done += 1
      } catch (error) {
        toast(errorMessage(error))
      }
    }
    setBulkCancel(false)
    setSelection(new Set())
    toast(
      `${done === 1 ? '1 envio cancelado' : `${done} envios cancelados`}. Os documentos continuam no histórico como cancelados.`,
    )
  }

  async function deleteBatch() {
    if (!bulkDelete || bulkDelete.trim().length < 10) return
    let done = 0
    for (const row of selectedRows) {
      for (const doc of row.documents.filter((d) => d.status === 'signed')) {
        try {
          await remove.mutateAsync({
            appointmentId: row.appointmentId,
            documentId: doc.id,
            justification: bulkDelete.trim(),
          })
          done += 1
        } catch (error) {
          toast(errorMessage(error))
        }
      }
    }
    setBulkDelete(null)
    setSelection(new Set())
    toast(
      `${done === 1 ? '1 documento excluído' : `${done} documentos excluídos`}. O registro de prova permanece na trilha.`,
    )
  }

  const options: FilterSegmentOption<SignatureFilter>[] = FILTERS.map((value) => ({
    value,
    label: FILTER_LABELS[value],
    count: meta?.counts[value],
  }))
  const unstable = meta
    ? Object.entries(meta.providers).find(([, p]) => p.state === 'unstable')
    : undefined
  const isEmptyPeriod = !query.isPending && !term && (meta?.counts.all ?? 0) === 0

  const pushButton =
    push.support === 'unsupported' || !push.isAvailable ? null : push.isSubscribed ? (
      <Button
        variant="text"
        size="sm"
        loading={push.disable.isPending}
        loadingLabel="Desligando…"
        onClick={() =>
          push.disable.mutate(undefined, {
            onSuccess: () => toast(PUSH_MESSAGES.off),
            onError: (error) => toast(errorMessage(error)),
          })
        }
      >
        Aviso no navegador ligado · desligar
      </Button>
    ) : (
      <Button
        variant="outline"
        size="sm"
        loading={push.enable.isPending}
        loadingLabel="Ligando…"
        onClick={() =>
          push.enable.mutate(undefined, {
            onSuccess: () => toast(PUSH_MESSAGES.on),
            onError: (error) => toast(error instanceof Error ? error.message : errorMessage(error)),
          })
        }
      >
        Avisar quando assinarem
      </Button>
    )

  const panel = selectedId ? (
    <SignaturePanel
      appointmentId={selectedId}
      whatsappMode={whatsappMode}
      provider={provider}
      onClose={() => openPanel(null)}
    />
  ) : null

  const list = (
    <>
      <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
        <FilterSegments
          label="Filtrar assinaturas"
          options={options}
          value={filter}
          onChange={setFilter}
        />
        <SearchInput
          className="tablet:max-w-[300px]"
          value={search}
          onChange={(value) => {
            setSearch(value)
            setPage(1)
          }}
          placeholder="Buscar por paciente, procedimento ou protocolo"
          aria-label="Buscar assinatura"
        />
      </div>
      {meta ? (
        <div className="mt-3">
          <PeriodFilter
            from={period.from ?? meta.period.from}
            to={period.to ?? meta.period.to}
            onApply={(from, to) => {
              setPeriod({ from, to })
              setPage(1)
            }}
            fromLabel="Envios a partir de"
            toLabel="Envios até"
            required
          />
        </div>
      ) : null}

      <div className="mt-4">
        {selection.size > 0 ? (
          <BulkBar
            summary={`${selection.size} ${selection.size === 1 ? 'assinatura selecionada' : 'assinaturas selecionadas'}`}
            onClear={() => setSelection(new Set())}
            actions={
              <>
                <Button
                  variant="outline"
                  size="sm"
                  loading={batch.isPending}
                  loadingLabel="Reenviando…"
                  onClick={() => resendBatch('whatsapp')}
                >
                  Reenviar · WhatsApp
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  loading={batch.isPending}
                  loadingLabel="Reenviando…"
                  onClick={() => resendBatch('email')}
                >
                  Reenviar · e-mail
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  loading={zip.isPending}
                  loadingLabel="Reunindo os PDFs…"
                  onClick={downloadZip}
                >
                  Baixar .zip
                </Button>
                {session.profile?.isRoot ? (
                  <Button variant="text" size="sm" onClick={() => setBulkDelete('')}>
                    Excluir…
                  </Button>
                ) : (
                  <Button variant="text" size="sm" onClick={() => setBulkCancel(true)}>
                    Cancelar…
                  </Button>
                )}
              </>
            }
          />
        ) : null}
        {query.isPending ? (
          <TableSkeleton rows={6} columns={6} />
        ) : rows.length === 0 ? (
          <EmptyState
            bordered
            title={
              term
                ? `Nenhuma assinatura para "${term}"`
                : filter === 'pending'
                  ? 'Nada pendente'
                  : filter === 'awaiting_patient'
                    ? 'Ninguém aguardando'
                    : filter === 'signed'
                      ? 'Nenhum assinado no período'
                      : 'Nenhum envio no período'
            }
            description={
              term
                ? 'A busca cobre o nome da paciente, o procedimento e o protocolo, dentro do filtro e do período.'
                : filter === 'pending'
                  ? 'Todos os documentos do período estão assinados ou aguardando o paciente.'
                  : 'Mude o período ou o filtro para ver outros envios.'
            }
            action={
              term ? (
                <Button variant="outline" onClick={() => setSearch('')}>
                  Limpar busca
                </Button>
              ) : filter !== 'all' ? (
                <Button variant="outline" onClick={() => setFilter('all')}>
                  Ver todos
                </Button>
              ) : undefined
            }
          />
        ) : (
          <Table>
            <TableHeader>
              <tr>
                <TableHead aria-label="Seleção" />
                <TableHead
                  sorted={sort === '-sentAt' ? 'desc' : sort === 'sentAt' ? 'asc' : null}
                  onSort={() => sortBy('sentAt')}
                >
                  Envio
                </TableHead>
                <TableHead
                  sorted={sort === 'patient' ? 'asc' : null}
                  onSort={() => sortBy('patient')}
                >
                  Paciente
                </TableHead>
                <TableHead>Procedimento</TableHead>
                <TableHead>Entrega</TableHead>
                <TableHead
                  sorted={sort === '-daysOpen' ? 'desc' : null}
                  onSort={() => sortBy('daysOpen')}
                >
                  Em aberto
                </TableHead>
                <TableHead
                  numeric
                  sorted={sort === '-totalAmountCents' ? 'desc' : null}
                  onSort={() => sortBy('total')}
                >
                  Total
                </TableHead>
                <TableHead>Status</TableHead>
                <TableHead aria-label="Ações" />
              </tr>
            </TableHeader>
            <TableBody>
              {rows.map((row) => {
                const name = titleCase(row.patient.name)
                const selectCell = (
                  <TableCell key="select" className="w-[36px]">
                    <Checkbox
                      aria-label={`Selecionar ${name}`}
                      checked={selection.has(row.appointmentId)}
                      onCheckedChange={() => toggle(row.appointmentId)}
                      onClick={(event) => event.stopPropagation()}
                    />
                  </TableCell>
                )
                const sentCellNode = (
                  <TableCell key="sent" label="Envio" muted>
                    {sentCell(row)}
                  </TableCell>
                )
                const patientCell = (
                  <TableCell key="patient" primary>
                    {name}
                  </TableCell>
                )
                const target = targetOf(row)
                return (
                  <TableRow
                    key={row.appointmentId}
                    critical={row.level === 'critical'}
                    selected={selection.has(row.appointmentId) || selectedId === row.appointmentId}
                    onClick={() => openPanel(row.appointmentId)}
                  >
                    {isDesk
                      ? [selectCell, sentCellNode, patientCell]
                      : [patientCell, selectCell, sentCellNode]}
                    <TableCell label="Procedimento" muted>
                      {row.procedure ?? (row.type === 'standalone' ? 'Documentos avulsos' : '—')}
                      {row.parentAppointmentId ? ' · documentos adicionados' : ''}
                    </TableCell>
                    <TableCell label="Entrega" muted>
                      {row.deliverySummary || (row.sentAt ? 'na fila' : 'não enviado')}
                      {row.pendingAction
                        ? ` · ${row.pendingAction.kind === 'cancel' ? 'cancelando…' : row.pendingAction.kind === 'resend' ? 'reenviando…' : 'enviando…'}`
                        : ''}
                    </TableCell>
                    <TableCell
                      label="Em aberto"
                      className={row.isUrgent ? 'text-plum' : undefined}
                      muted={!row.isUrgent}
                    >
                      {daysOpenText(row.daysOpen)}
                    </TableCell>
                    <TableCell label="Total" numeric>
                      {row.totalAmountCents !== null
                        ? formatCurrency(row.totalAmountCents / 100)
                        : ''}
                    </TableCell>
                    <TableCell label="Status">
                      <StatusTag level={row.level}>{row.label}</StatusTag>
                    </TableCell>
                    <TableCell>
                      <RowActions>
                        {row.actions.includes('sign') ? (
                          <button
                            type="button"
                            disabled={actions.isBusy}
                            onClick={() =>
                              actions.askSign({
                                ...target,
                                clinicName: session.profile?.activeClinic?.name,
                              })
                            }
                          >
                            assinar
                          </button>
                        ) : null}
                        {row.actions.includes('resend') ? (
                          <button
                            type="button"
                            disabled={actions.isBusy}
                            onClick={() => actions.askResend(target)}
                          >
                            reenviar
                          </button>
                        ) : null}
                        {row.actions.includes('send') ? (
                          <button
                            type="button"
                            onClick={() =>
                              navigate(
                                row.type === 'procedure'
                                  ? routes.generateDocuments(row.patient.id, row.appointmentId)
                                  : routes.standaloneDocuments(row.patient.id, row.appointmentId),
                              )
                            }
                          >
                            enviar
                          </button>
                        ) : null}
                        <button type="button" onClick={() => openPanel(row.appointmentId)}>
                          abrir
                        </button>
                      </RowActions>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        )}
        {query.data && rows.length > 0 ? (
          <ListFooter
            meta={query.data.meta}
            noun={['assinatura', 'assinaturas']}
            note={`${SORT_NOTES[sort]} · a linha abre o painel; "reenviar" e "assinar" ficam à vista ao passar o mouse`}
            onPage={setPage}
          />
        ) : null}
      </div>
    </>
  )

  return (
    <>
      <PageHeader crumbs={[{ label: 'Operação' }, { label: 'Assinaturas' }]} actions={pushButton} />
      <Page wide>
        <PageTitle title="Assinaturas" subtitle={subtitle(meta?.counts)} />
        <p className="font-body text-secondary mt-2 text-[12px] leading-[1.6] font-light">
          {CRITERION}
        </p>

        {unstable ? (
          <SystemBanner
            className="mt-5"
            title={`Envio por ${unstable[0] === 'whatsapp' ? 'WhatsApp' : 'e-mail'} indisponível`}
            description={`Instabilidade no provedor${unstable[1].since ? ` desde ${new Date(unstable[1].since).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}` : ''}. Os envios ficam em fila e saem sozinhos. Você pode enviar por ${unstable[0] === 'whatsapp' ? 'e-mail' : 'WhatsApp'} agora.`}
          />
        ) : null}

        {query.isError ? (
          <LoadError
            className="mt-6"
            title="Não conseguimos carregar as assinaturas"
            error={query.error}
            onRetry={() => void query.refetch()}
          />
        ) : isEmptyPeriod && filter === 'all' && !period.from ? (
          <EmptyState
            className="mt-6"
            title="Nenhum envio ainda"
            description="Gere os documentos pela ficha de uma paciente e envie na etapa 3. O envio aparece aqui com a entrega, a leitura e a assinatura."
          />
        ) : isWide && panel ? (
          <div className="mt-2 grid grid-cols-[minmax(0,1fr)_380px] items-start gap-6">
            <div className="min-w-0">{list}</div>
            <div className="sticky top-[calc(var(--height-header,57px)+16px)]">{panel}</div>
          </div>
        ) : (
          <>
            {panel ? <div className="mt-6">{panel}</div> : null}
            {list}
          </>
        )}
      </Page>

      {actions.dialogs}

      <ConfirmDialog
        open={bulkCancel}
        onOpenChange={(open) => {
          if (!cancel.isPending) setBulkCancel(open)
        }}
        title={`Cancelar ${selection.size === 1 ? 'o envio selecionado' : `os ${selection.size} envios selecionados`}?`}
        effect="Os links deixam de funcionar e os documentos ficam no histórico como cancelados. Nada assinado se desfaz; os atendimentos continuam. Envios que a paciente já assinou ficam de fora: eles pedem motivo, um a um."
        confirmLabel="Cancelar envios"
        cancelLabel="Voltar"
        loading={cancel.isPending}
        loadingLabel="Cancelando…"
        onConfirm={() => void cancelBatch()}
      />

      <ConfirmDialog
        open={bulkDelete !== null}
        onOpenChange={(open) => {
          if (!open && !remove.isPending) setBulkDelete(null)
        }}
        title={`Excluir os documentos assinados de ${selection.size === 1 ? '1 assinatura' : `${selection.size} assinaturas`}?`}
        effect="Os documentos assinados somem da ficha, das listas e dos downloads. O registro de prova permanece na trilha e não pode ser apagado. Nada volta."
        confirmLabel="Excluir"
        cancelLabel="Voltar"
        loading={remove.isPending}
        loadingLabel="Excluindo…"
        onConfirm={() => void deleteBatch()}
      >
        <Field
          id={justificationId}
          label="Justificativa"
          hint="Vale para todos os documentos do lote e fica na trilha de cada um."
          error={
            bulkDelete !== null && bulkDelete !== '' && bulkDelete.trim().length < 10
              ? 'Diga o motivo em pelo menos 10 caracteres.'
              : null
          }
        >
          <Textarea
            id={justificationId}
            rows={3}
            value={bulkDelete ?? ''}
            onChange={(event) => setBulkDelete(event.target.value)}
          />
        </Field>
      </ConfirmDialog>
    </>
  )
}
